import React, { useState } from 'react';
import { X, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const CreateAccountModal = ({ isOpen, onClose, onSuccess, users }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    user_id: '',
    account_type: 'instant',
    account_size: '10000',
    plan_name: '',
    login_id: '',
    password: '',
    platform: 'MT5',
    daily_drawdown_percent: 3,
    daily_drawdown_amount: 300,
    max_drawdown_percent: 5,
    max_drawdown_amount: 500,
    profit_split: 80,
    profit_target: null,
    notes: ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const updated = { ...prev, [name]: value };
      
      // Auto-calculate drawdown amounts when account size or type changes
      if (name === 'account_size' || name === 'account_type') {
        const size = name === 'account_size' ? parseInt(value) || 0 : parseInt(prev.account_size) || 0;
        const type = name === 'account_type' ? value : prev.account_type;
        
        if (type === 'instant') {
          updated.daily_drawdown_percent = 3;
          updated.max_drawdown_percent = 5;
          updated.daily_drawdown_amount = size * 0.03;
          updated.max_drawdown_amount = size * 0.05;
        } else {
          updated.daily_drawdown_percent = 5;
          updated.max_drawdown_percent = 8;
          updated.daily_drawdown_amount = size * 0.05;
          updated.max_drawdown_amount = size * 0.08;
        }
      }
      
      // Update plan name based on type and size
      if (name === 'account_type' || name === 'account_size') {
        const type = name === 'account_type' ? value : prev.account_type;
        const size = name === 'account_size' ? value : prev.account_size;
        updated.plan_name = `${type.toUpperCase()} $${parseInt(size).toLocaleString()}`;
      }
      
      return updated;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await axios.post(`${API_URL}/admin/accounts`, {
        ...formData,
        account_size: parseInt(formData.account_size),
        profit_target: formData.account_type === 'onestep' ? 10 : null
      });
      
      alert('Trading account created successfully!');
      onSuccess();
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create account');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const inputStyle = {
    width: '100%',
    padding: '12px',
    background: 'var(--bg-primary)',
    border: '1px solid var(--border-primary)',
    borderRadius: '8px',
    color: 'var(--text-primary)',
    fontSize: '14px'
  };

  const labelStyle = {
    display: 'block',
    fontSize: '14px',
    fontWeight: 600,
    color: 'var(--text-primary)',
    marginBottom: '8px'
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.8)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 9999,
      padding: '20px',
      overflowY: 'auto'
    }}>
      <div style={{
        background: 'var(--bg-secondary)',
        borderRadius: '20px',
        padding: '32px',
        maxWidth: '600px',
        width: '100%',
        maxHeight: '90vh',
        overflowY: 'auto',
        border: '1px solid var(--border-subtle)'
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '24px'
        }}>
          <h2 style={{
            fontSize: '24px',
            fontWeight: 700,
            color: 'var(--text-primary)'
          }}>
            Create Trading Account
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--text-muted)',
              cursor: 'pointer',
              padding: '8px'
            }}
          >
            <X size={24} />
          </button>
        </div>

        {error && (
          <div style={{
            background: 'rgba(239, 68, 68, 0.1)',
            border: '1px solid rgba(239, 68, 68, 0.3)',
            borderRadius: '12px',
            padding: '16px',
            marginBottom: '24px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <AlertCircle size={20} color="var(--error)" />
            <span style={{ color: 'var(--error)', fontSize: '14px' }}>{error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {/* User Selection */}
          <div style={{ marginBottom: '20px' }}>
            <label style={labelStyle}>User *</label>
            <select
              name="user_id"
              value={formData.user_id}
              onChange={handleChange}
              required
              style={inputStyle}
            >
              <option value="">Select User</option>
              {users.filter(u => u.role !== 'admin').map(user => (
                <option key={user.id} value={user.id}>
                  {user.full_name} ({user.email})
                </option>
              ))}
            </select>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            {/* Account Type */}
            <div style={{ marginBottom: '20px' }}>
              <label style={labelStyle}>Account Type *</label>
              <select
                name="account_type"
                value={formData.account_type}
                onChange={handleChange}
                required
                style={inputStyle}
              >
                <option value="instant">INSTANT</option>
                <option value="onestep">1-STEP</option>
              </select>
            </div>

            {/* Account Size */}
            <div style={{ marginBottom: '20px' }}>
              <label style={labelStyle}>Account Size *</label>
              <select
                name="account_size"
                value={formData.account_size}
                onChange={handleChange}
                required
                style={inputStyle}
              >
                <option value="5000">$5,000</option>
                <option value="10000">$10,000</option>
                <option value="25000">$25,000</option>
                <option value="50000">$50,000</option>
              </select>
            </div>
          </div>

          {/* MT5 Credentials Section */}
          <div style={{
            background: 'rgba(212, 175, 55, 0.05)',
            border: '1px solid rgba(212, 175, 55, 0.2)',
            borderRadius: '12px',
            padding: '20px',
            marginBottom: '20px'
          }}>
            <h3 style={{
              fontSize: '16px',
              fontWeight: 600,
              color: 'var(--accent-gold)',
              marginBottom: '16px'
            }}>
              MT5 Credentials
            </h3>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              {/* Login ID */}
              <div style={{ marginBottom: '16px' }}>
                <label style={labelStyle}>Login ID *</label>
                <input
                  type="text"
                  name="login_id"
                  value={formData.login_id}
                  onChange={handleChange}
                  required
                  placeholder="e.g., 12345678"
                  style={inputStyle}
                />
              </div>

              {/* Platform */}
              <div style={{ marginBottom: '16px' }}>
                <label style={labelStyle}>Platform *</label>
                <select
                  name="platform"
                  value={formData.platform}
                  onChange={handleChange}
                  required
                  style={inputStyle}
                >
                  <option value="MT5">MT5</option>
                  <option value="Deriv">Deriv</option>
                </select>
              </div>
            </div>

            {/* Trading Password */}
            <div style={{ marginBottom: '16px' }}>
              <label style={labelStyle}>
                Trading Password *
                <span style={{ 
                  fontSize: '11px', 
                  color: 'var(--text-muted)',
                  fontWeight: 400,
                  marginLeft: '8px'
                }}>
                  (Visible to trader)
                </span>
              </label>
              <input
                type="text"
                name="password"
                value={formData.password}
                onChange={handleChange}
                required
                placeholder="Main trading password"
                style={inputStyle}
              />
            </div>
          </div>

          {/* Notes */}
          <div style={{ marginBottom: '24px' }}>
            <label style={labelStyle}>Notes (Optional)</label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              placeholder="Add any notes about this account..."
              rows={3}
              style={{
                ...inputStyle,
                resize: 'vertical'
              }}
            />
          </div>

          {/* Auto-calculated values display */}
          <div style={{
            background: 'rgba(14, 165, 233, 0.1)',
            border: '1px solid rgba(14, 165, 233, 0.3)',
            borderRadius: '12px',
            padding: '16px',
            marginBottom: '24px'
          }}>
            <div style={{
              fontSize: '13px',
              color: 'var(--text-secondary)',
              marginBottom: '8px'
            }}>
              <strong style={{ color: 'var(--accent-blue)' }}>Account Rules (Auto-calculated):</strong>
            </div>
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(2, 1fr)', 
              gap: '8px',
              fontSize: '13px', 
              color: 'var(--text-secondary)' 
            }}>
              <div>Daily DD: ${formData.daily_drawdown_amount.toFixed(0)} ({formData.daily_drawdown_percent}%)</div>
              <div>Max DD: ${formData.max_drawdown_amount.toFixed(0)} ({formData.max_drawdown_percent}%)</div>
              <div>Profit Split: {formData.profit_split}%</div>
              <div>Profit Target: {formData.account_type === 'onestep' ? '10%' : 'N/A'}</div>
            </div>
          </div>

          {/* Buttons */}
          <div style={{
            display: 'flex',
            gap: '12px',
            justifyContent: 'flex-end'
          }}>
            <Button
              type="button"
              onClick={onClose}
              variant="ghost"
              style={{ padding: '12px 24px' }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="btn-primary"
              style={{
                padding: '12px 24px',
                opacity: loading ? 0.7 : 1
              }}
            >
              {loading ? 'Creating...' : 'Create Account'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateAccountModal;
